import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatCurrency } from "@/lib/documentCalc";
import {
  erstelleMahnungsEntwuerfe,
  formatMahnungDate,
  getDokumentMahnung,
  setMahnungPause,
  setzeZahlungsfrist,
  mahnungStatusLabel,
  type DokumentMahnungStatus,
} from "@/lib/mahnwesen";
import { notifyMahnwesenBadgeRefresh } from "@/lib/navBadges";
import { toast } from "@/lib/toast";
import { ZahlungsfristDialog } from "@/pages/dokumente/mahnung/ZahlungsfristDialog";

export function DocumentMahnungPanel({
  docId,
  enabled,
  zIndexClass,
  onChanged,
}: {
  docId: number;
  enabled: boolean;
  zIndexClass?: string;
  onChanged?: () => void | Promise<void>;
}) {
  void zIndexClass;
  const navigate = useNavigate();
  const [status, setStatus] = useState<DokumentMahnungStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fristOpen, setFristOpen] = useState(false);
  const [fristIntent, setFristIntent] = useState<"frist" | "fortsetzen">("frist");

  const reload = useCallback(async () => {
    if (!enabled || !docId) {
      setStatus(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setStatus(await getDokumentMahnung(docId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mahnstatus konnte nicht geladen werden.");
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [docId, enabled]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (!enabled) return null;

  async function handlePause(pausiert: boolean) {
    setBusy(true);
    try {
      await setMahnungPause(docId, pausiert);
      toast.success(pausiert ? "Mahnung pausiert." : "Mahnung fortgesetzt.");
      notifyMahnwesenBadgeRefresh();
      setFristOpen(false);
      await reload();
      await onChanged?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Aktion fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  async function handleFrist(body: { faelligkeitsdatum: string; pause_aufheben: boolean }) {
    setBusy(true);
    try {
      await setzeZahlungsfrist(docId, body);
      toast.success(
        body.pause_aufheben
          ? `Fällig am ${formatMahnungDate(body.faelligkeitsdatum)}, Mahnung fortgesetzt.`
          : `Fällig am ${formatMahnungDate(body.faelligkeitsdatum)} gesetzt.`,
      );
      notifyMahnwesenBadgeRefresh();
      setFristOpen(false);
      await reload();
      await onChanged?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Zahlungsfrist konnte nicht gesetzt werden.");
    } finally {
      setBusy(false);
    }
  }

  async function handleEntwurfErstellen() {
    setBusy(true);
    try {
      const result = await erstelleMahnungsEntwuerfe({ dokument_ids: [docId], sammeln: false });
      notifyMahnwesenBadgeRefresh();
      const erster = result.entwuerfe[0];
      if (erster) {
        toast.success("Mahnentwurf erstellt.");
        navigate(`/dokumente/mahnwesen/${erster.id}`);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Entwurf konnte nicht erstellt werden.");
    } finally {
      setBusy(false);
    }
  }

  const offeneEntwuerfe = (status?.historie || []).filter((m) => m.status === "entwurf");
  const ersterEntwurf = offeneEntwuerfe[0];
  const canCreate =
    Boolean(status?.naechste_stufe) &&
    !status?.mahnung_pausiert &&
    !status?.mahnsperre_aktiv &&
    Number(status?.summe_offen || 0) > 0 &&
    Boolean(status?.permissions?.erstellen);

  return (
    <>
    <div className="mt-3 rounded-xl border border-separator bg-card p-4 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-label">Mahnwesen</h3>
        {status ? (
          <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-xs font-medium">
            {status.mahnstufe_label}
            {status.mahnung_faellig ? " · fällig" : ""}
          </span>
        ) : null}
      </div>

      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-label-secondary">
          <Spinner /> Laden …
        </div>
      ) : error ? (
        <Alert variant="error" className="mt-3">
          {error}
        </Alert>
      ) : status ? (
        <div className="mt-3 space-y-3">
          <div className="grid gap-1 text-xs text-label-secondary sm:grid-cols-2">
            <div>Fällig: {formatMahnungDate(status.faelligkeitsdatum)}</div>
            <div>
              Überfällig: {status.tage_ueberfaellig > 0 ? `${status.tage_ueberfaellig} Tage` : "—"}
            </div>
            <div>Offen: {formatCurrency(status.summe_offen, "EUR")}</div>
            <div>Bezahlt: {formatCurrency(status.summe_bezahlt, "EUR")}</div>
          </div>

          {status.mahnsperre_aktiv ? (
            <Alert variant="warning" className="text-xs">
              Für diese Rechnung oder diesen Kunden besteht eine aktive Mahnsperre.
            </Alert>
          ) : null}

          {status.naechste_stufe_label ? (
            <p className="text-xs text-label-secondary">
              Nächste Stufe: <strong className="text-label">{status.naechste_stufe_label}</strong>
              {status.empfohlen_am ? ` ab ${formatMahnungDate(status.empfohlen_am)}` : ""}
            </p>
          ) : (
            <p className="text-xs text-label-secondary">Höchste Mahnstufe erreicht.</p>
          )}

          <div className="flex flex-wrap gap-2">
            {ersterEntwurf ? (
              <Button
                type="button"
                size="sm"
                onClick={() => navigate(`/dokumente/mahnwesen/${ersterEntwurf.id}`)}
              >
                Entwurf öffnen
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                disabled={!canCreate || busy}
                loading={busy}
                onClick={() => void handleEntwurfErstellen()}
              >
                {status.naechste_stufe_label || "Mahnung"} vorbereiten
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={!status.permissions?.sperren_verwalten || busy}
              onClick={() => {
                if (status.mahnung_pausiert) {
                  setFristIntent("fortsetzen");
                  setFristOpen(true);
                } else {
                  void handlePause(true);
                }
              }}
            >
              {status.mahnung_pausiert ? "Fortsetzen" : "Pausieren"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={!status.permissions?.sperren_verwalten || busy}
              onClick={() => {
                setFristIntent("frist");
                setFristOpen(true);
              }}
            >
              Frist anpassen
            </Button>
          </div>

          {status.historie.length ? (
            <div className="border-t border-separator pt-3">
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-label-secondary">
                Mahnhistorie
              </h4>
              <ul className="space-y-1.5">
                {status.historie.slice(0, 6).map((m) => (
                  <li key={m.id} className="text-xs text-label-secondary">
                    <button
                      type="button"
                      className="font-medium text-accent hover:underline"
                      onClick={() => navigate(`/dokumente/mahnwesen/${m.id}`)}
                    >
                      {m.nummer || `Entwurf #${m.id}`}
                    </button>
                    {" · "}
                    {m.stufe_bezeichnung}
                    {" · "}
                    {mahnungStatusLabel(m.status, true)}
                    {" · "}
                    {formatMahnungDate(m.versendet_am || m.belegdatum)}
                    {" · "}
                    {formatCurrency(m.summe_gesamt, "EUR")}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="mt-3 text-xs text-label-secondary">Kein Mahnstatus verfügbar.</p>
      )}
    </div>
    {status ? (
      <ZahlungsfristDialog
        open={fristOpen}
        onClose={() => setFristOpen(false)}
        dokumentNummer={status.dokument_nummer}
        kundeName={null}
        aktuelleFaelligkeit={status.faelligkeitsdatum}
        pausiert={status.mahnung_pausiert}
        intent={fristIntent}
        busy={busy}
        onSubmit={(body) => void handleFrist(body)}
        onFortsetzenOhneFrist={() => void handlePause(false)}
      />
    ) : null}
    </>
  );
}
